package com.fantasytenis.web.hooks

import androidx.compose.runtime.*
import kotlinx.browser.window
import org.w3c.dom.events.Event

/**
 * Data class holding window dimensions.
 */
data class WindowSize(
    val width: Int,
    val height: Int
)

/**
 * Composable hook that returns the current window size and updates on resize.
 */
@Composable
fun rememberWindowSize(): WindowSize {
    var windowSize by remember {
        mutableStateOf(
            WindowSize(
                width = window.innerWidth,
                height = window.innerHeight
            )
        )
    }

    DisposableEffect(Unit) {
        val callback: (Event) -> Unit = {
            windowSize = WindowSize(
                width = window.innerWidth,
                height = window.innerHeight
            )
        }

        window.addEventListener("resize", callback)

        onDispose {
            window.removeEventListener("resize", callback)
        }
    }

    return windowSize
}

/**
 * Composable hook that returns just the window width.
 */
@Composable
fun rememberWindowWidth(): Int {
    var width by remember { mutableStateOf(window.innerWidth) }

    DisposableEffect(Unit) {
        val callback: (Event) -> Unit = {
            width = window.innerWidth
        }

        window.addEventListener("resize", callback)

        onDispose {
            window.removeEventListener("resize", callback)
        }
    }

    return width
}

/**
 * Composable that returns true if the viewport is considered "mobile" (< 768px).
 */
@Composable
fun isMobile(): Boolean = rememberWindowWidth() < Breakpoints.MOBILE

/**
 * Composable that returns true if the viewport is considered "tablet" (768px - 1023px).
 */
@Composable
fun isTablet(): Boolean {
    val width = rememberWindowWidth()
    return width >= Breakpoints.MOBILE && width < Breakpoints.TABLET
}

/**
 * Composable that returns true if the viewport is considered "desktop" (>= 1024px).
 */
@Composable
fun isDesktop(): Boolean = rememberWindowWidth() >= Breakpoints.TABLET
